/**
 * Phase 4 — Agent Settings tab.
 *
 * Shows workspace-wide AI automation defaults. Each toggle writes immediately
 * on change (no Save button) — pattern matches the Auto-Prospecting panel in
 * the campaign editor.
 *
 * New campaigns inherit these defaults; existing campaigns keep whatever was
 * set on them at creation time.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

type SettingKey =
  | 'defaultAutoProspect'
  | 'defaultAutoSendEmails'
  | 'defaultAutoReply'
  | 'autoCreateFromWebsite'
  | 'autoSplitTest';

type Settings = Record<SettingKey, boolean>;

const FIELDS: { key: SettingKey; title: string; description: string; comingSoon?: boolean }[] = [
  {
    key: 'defaultAutoProspect',
    title: 'Default: Prospecting Agent on',
    description: 'New campaigns continuously discover new prospects matching their ICP by default.',
  },
  {
    key: 'defaultAutoSendEmails',
    title: 'Default: Auto-send AI-personalised emails',
    description: 'Send AI-personalised emails without per-email approval. Static-text steps always send.',
  },
  {
    key: 'defaultAutoReply',
    title: 'Default: Auto-reply to responses',
    description: 'When prospects reply, AI handles the first response. Wired in Phase 6.',
  },
  {
    key: 'autoCreateFromWebsite',
    title: 'Auto-create campaigns from a business URL',
    description: 'When a user provides a website, the agent scrapes it and proposes a full campaign. Coming soon.',
    comingSoon: true,
  },
  {
    key: 'autoSplitTest',
    title: 'Auto-suggest split tests across campaigns',
    description: 'Agent monitors performance and proposes A/B test variants. Coming soon.',
    comingSoon: true,
  },
];

export function AgentSettingsTab() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [savingKey, setSavingKey] = useState<SettingKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/workspace/agent-settings', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (!cancelled) setSettings(data); })
      .catch(err => { if (!cancelled) toast.error(`Failed to load settings: ${err.message}`); });
    return () => { cancelled = true; };
  }, []);

  const update = async (key: SettingKey, value: boolean) => {
    if (!settings) return;
    const previous = settings[key];
    setSettings({ ...settings, [key]: value });
    setSavingKey(key);
    try {
      const res = await fetch('/api/workspace/agent-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (err) {
      setSettings(s => (s ? { ...s, [key]: previous } : s));
      toast.error(`Could not update: ${(err as Error).message}`);
    } finally {
      setSavingKey(null);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-6 space-y-6">
      <div>
        <h2 className="text-base font-semibold mb-1">Workspace AI defaults</h2>
        <p className="text-sm text-muted-foreground">
          Applies to new campaigns. Existing campaigns keep their current settings.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl divide-y divide-border">
        {FIELDS.map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-4 p-5">
            <div className="min-w-0">
              <p className="text-sm font-medium flex items-center gap-2">
                {f.title}
                {f.comingSoon && (
                  <span className="text-[10px] bg-surface-subtle text-content-secondary px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">
                    Coming soon
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{f.description}</p>
            </div>
            <div className="flex items-center gap-2">
              {savingKey === f.key && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
              <Switch
                checked={settings[f.key]}
                onCheckedChange={(v) => update(f.key, v)}
                disabled={f.comingSoon}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
